import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { 
  Loader2, 
  Search, 
  Filter, 
  Crown, 
  Clock, 
  AlertCircle,
  Check,
  ArrowUpCircle,
  Calendar,
  Users,
  RefreshCw
} from 'lucide-react';

interface UserPlan {
  user_id: string;
  email: string;
  nome: string;
  plan_type: string;
  plan_name: string;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

const UserPlansPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [users, setUsers] = useState<UserPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'trial' | 'free' | 'expired'>('all');
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserPlan | null>(null);
  const [newPlanType, setNewPlanType] = useState<string>('');
  const [durationDays, setDurationDays] = useState<number>(30);

  const fetchUserPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc('admin_get_all_user_plans');

      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      console.error('Error fetching user plans:', error);
      setError('Erro ao carregar planos dos usuários: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPlans();
  }, [supabase]);

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlanType) return;

    setUpdatingUser(selectedUser.user_id);
    try {
      const { data, error } = await supabase.rpc('admin_update_user_plan', {
        target_user_id: selectedUser.user_id,
        new_plan_type: newPlanType,
        duration_days: durationDays
      });

      if (error) throw error;

      // Atualizar a lista de usuários
      await fetchUserPlans();
      setShowUpdateModal(false);
    } catch (error: any) {
      console.error('Error updating user plan:', error);
      setError('Erro ao atualizar plano: ' + error.message);
    } finally {
      setUpdatingUser(null);
    }
  };

  const openUpdateModal = (user: UserPlan) => {
    setSelectedUser(user);
    setNewPlanType(user.plan_type);
    setShowUpdateModal(true);
  };

  const filteredUsers = users.filter(user => {
    // Filtro de busca
    const matchesSearch = 
      user.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtro de status
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'active':
          matchesStatus = ['premium', 'professional', 'basic', 'lifetime'].includes(user.plan_type);
          break;
        case 'trial':
          matchesStatus = user.plan_type === 'trial';
          break;
        case 'free':
          matchesStatus = user.plan_type === 'free';
          break;
        case 'expired':
          matchesStatus = !user.is_active && user.plan_type !== 'lifetime';
          break;
      }
    }

    return matchesSearch && matchesStatus;
  });

  const getPlanBadgeColor = (planType: string) => {
    switch (planType) {
      case 'premium':
        return 'bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400';
      case 'professional':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400';
      case 'basic':
        return 'bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400';
      case 'trial':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400';
      case 'lifetime':
        return 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400';
      default:
        return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcula dias restantes até a expiração
  const getDaysRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    
    const expiration = new Date(expiresAt);
    const now = new Date();
    
    if (isNaN(expiration.getTime())) return null;
    
    const diffTime = expiration.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  // Calcula a duração total em dias com base no tipo de plano
  const getTotalDuration = (planType: string) => {
    switch (planType) {
      case 'premium':
        return 12 * 30; // 12 meses em dias
      case 'professional':
        return 12 * 30; // 12 meses em dias
      case 'basic':
        return 1 * 30; // 1 mês em dias
      case 'trial':
        return 21; // 21 dias padrão para trial
      default:
        return 1 * 30;
    }
  };

  // Calcula a porcentagem de progresso
  const getProgressPercentage = (expiresAt: string | null, planType: string) => {
    if (planType === 'lifetime' || !expiresAt) {
      return 0; // Não mostra progresso para lifetime
    }
    
    const daysLeft = getDaysRemaining(expiresAt) || 0;
    const totalDuration = getTotalDuration(planType);
    
    return Math.min(100, Math.max(0, (daysLeft / totalDuration) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Planos dos Usuários</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Gerencie os planos e assinaturas dos usuários
        </p>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      <div className="card p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={fetchUserPlans}
              className="btn-outline"
              title="Atualizar dados"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </button>
            <div className="text-sm text-gray-500">
              {users.length} usuários encontrados
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar usuários..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filtro de Status */}
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="all">Todos os Status</option>
              <option value="active">Planos Pagos</option>
              <option value="trial">Trial</option>
              <option value="free">Gratuito</option>
              <option value="expired">Expirado</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-light">
                <th className="text-left p-4 font-medium">Usuário</th>
                <th className="text-left p-4 font-medium">Plano</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-left p-4 font-medium">Expira em</th>
                <th className="text-left p-4 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-dark-border">
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-dark-light">
                  <td className="p-4">
                    <div>
                      <p className="font-medium">{user.nome || 'Usuário'}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-xs text-gray-400">
                        Desde {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-yellow-500" />
                      <span>{user.plan_name}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      getPlanBadgeColor(user.plan_type)
                    }`}>
                      {user.is_active ? 'Ativo' : 'Expirado'}
                    </span>
                  </td>
                  <td className="p-4">
                    {user.plan_type === 'lifetime' ? (
                      <span className="text-purple-600 dark:text-purple-400">Vitalício</span>
                    ) : user.expires_at ? (
                      <div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>{formatDate(user.expires_at)}</span>
                        </div>
                        {getDaysRemaining(user.expires_at) !== null && (
                          <div className="mt-1">
                            <div className="text-xs text-gray-500">
                              {getDaysRemaining(user.expires_at)} dias restantes
                            </div>
                            <div className="h-1 bg-gray-200 dark:bg-dark-light rounded-full overflow-hidden mt-1">
                              <div
                                className="h-full bg-primary-500"
                                style={{ width: `${getProgressPercentage(user.expires_at, user.plan_type)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => openUpdateModal(user)}
                      className="btn-primary text-sm"
                      disabled={updatingUser === user.user_id}
                    >
                      {updatingUser === user.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                      )}
                      Alterar Plano
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Atualização de Plano */}
      {showUpdateModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b dark:border-dark-border">
              <h2 className="text-xl font-bold">Atualizar Plano</h2>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Usuário:</p>
                <div className="p-3 bg-gray-50 dark:bg-dark-light rounded-lg">
                  <p className="font-medium">{selectedUser.nome}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Plano Atual:</p>
                <div className="p-3 bg-gray-50 dark:bg-dark-light rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      getPlanBadgeColor(selectedUser.plan_type)
                    }`}>
                      {selectedUser.plan_name}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      selectedUser.is_active 
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    }`}>
                      {selectedUser.is_active ? 'Ativo' : 'Expirado'}
                    </span>
                  </div>
                  {selectedUser.expires_at && selectedUser.plan_type !== 'lifetime' && (
                    <div className="mt-2">
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>Expira em: {formatDate(selectedUser.expires_at)}</span>
                      </div>
                      {getDaysRemaining(selectedUser.expires_at) !== null && (
                        <div className="mt-1">
                          <div className="text-xs text-gray-500">
                            {getDaysRemaining(selectedUser.expires_at)} dias restantes
                          </div>
                          <div className="h-1 bg-gray-200 dark:bg-dark-light rounded-full overflow-hidden mt-1">
                            <div
                              className="h-full bg-primary-500"
                              style={{ width: `${getProgressPercentage(selectedUser.expires_at, selectedUser.plan_type)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Novo Plano:
                </label>
                <select
                  className="input w-full"
                  value={newPlanType}
                  onChange={(e) => setNewPlanType(e.target.value)}
                >
                  <option value="free">Gratuito</option>
                  <option value="trial">Trial</option>
                  <option value="basic">Basic</option>
                  <option value="professional">Professional</option>
                  <option value="premium">Premium</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Duração (dias):
                </label>
                <input
                  type="number"
                  className="input w-full"
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value))}
                  min={1}
                  max={365}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Aplicável para planos Trial e pagos. Lifetime e Free ignoram este valor.
                </p>
              </div>

              {/* Visualização da expiração */}
              {newPlanType !== 'lifetime' && newPlanType !== 'free' && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-light rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <span className="font-medium">Previsão de Expiração</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Data: {(() => {
                      const now = new Date();
                      now.setDate(now.getDate() + durationDays);
                      return now.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      });
                    })()}
                  </p>
                  
                  {/* Barra de progresso */}
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Início</span>
                      <span>Duração Total: {getTotalDuration(newPlanType)} dias</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-dark-light rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 transition-all duration-300"
                        style={{ width: `${(durationDays / getTotalDuration(newPlanType)) * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Hoje</span>
                      <span>{durationDays} dias</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="btn-outline"
                  disabled={!!updatingUser}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdatePlan}
                  className="btn-primary"
                  disabled={!!updatingUser}
                >
                  {updatingUser ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPlansPage;